
















































































































































import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch
} from '~/utils/nuxt3-migration'
import CSlider from '~/components/shared/configurable/slider/CSlider.vue'
import LoanCalculatorTimeButton from '~/components/car/classifieds/view/partials/loans/calculator/LoanCalculatorTimeButton.vue'
import LoanService from '~/services/loan/LoanService'
import { useDep } from '~/compositions/dependency-container'
import { formatNumber } from '~/utils/number'
import { debounce } from '~/utils/function'
import { DEBOUNCE_SWIFT } from '~/constants/debounce'
import { useLogger } from '~/compositions/logger'
import { useI18n } from '~/compositions/i18n'
import {
  OnlineLoanType,
  SantanderLoanInfo,
  SantanderLoanPeriodType
} from '~/constants/loan/loans'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { SantanderLoanInstallments } from '~/models/loan'
import SantanderLoanDetailsModal from '~/components/car/classifieds/view/partials/loans/calculator/santander/SantanderLoanDetailsModal.vue'
import SantanderLoanUserInfoModal from '~/components/car/classifieds/view/partials/loans/calculator/santander/SantanderLoanUserInfoModal.vue'
import { useSnackbar } from '~/compositions/snackbar'
import LoanPerks from '~/components/car/classifieds/view/partials/loans/perks/LoanPerks.vue'

export default defineComponent({
  components: {
    LoanPerks,
    SantanderLoanUserInfoModal,
    SantanderLoanDetailsModal,
    LoanCalculatorTimeButton,
    CSlider
  },
  props: {
    loanInfo: {
      type: Object as PropType<SantanderLoanInfo>,
      required: true
    },
    sessionId: {
      type: String,
      required: true
    },
    loanType: {
      type: String as PropType<OnlineLoanType>,
      required: false,
      default: OnlineLoanType.AUTO
    }
  },
  setup(props) {
    const { loanInfo } = toRefs(props)
    const loanService = useDep(LoanService)
    const logger = useLogger()
    const snackbar = useSnackbar()
    const { tc } = useI18n()

    const { state } = useNamespacedStore<ClassifiedViewState>(
      CLASSIFIED_VIEW_NS
    )
    const classifiedRawPrice = computed(
      () => state.classified?.price.extra.raw_price || 0
    )
    const classifiedId = computed(() => state.classifiedId)
    const targetUrl = ref(null)

    const loanAmount = ref(classifiedRawPrice.value) // init with the classified price
    const loanDownpayment = ref(
      Number((0.1 * classifiedRawPrice.value).toFixed(0))
    )
    const loanInstallments = ref<SantanderLoanInstallments | null>(null)
    const showInput = ref(false)
    const inputRef = ref(null)

    const minLoanDuration = computed<number>(() => {
      return loanInfo.value.period.type === SantanderLoanPeriodType.OBJECT
        ? Number(loanInfo.value.period.values.min)
        : Number(loanInfo.value.period.values[0])
    })
    const maxLoanDuration = computed<number>(() => {
      return loanInfo.value.period.type === SantanderLoanPeriodType.OBJECT
        ? Number(loanInfo.value.period.values.max)
        : Number(
            loanInfo.value.period.values[
              loanInfo.value.period.values.length - 1
            ]
          )
    })

    const loanDuration = ref(maxLoanDuration.value)
    const liveLoanDuration = ref(maxLoanDuration.value)

    const loadingLoanInfo = ref(false)
    const showUserInfoModal = ref(false)
    const showDetailsModal = ref(false)

    const sliderRef = ref<typeof CSlider | null>(null)

    onMounted(() => {
      onBlur() // to get correct downpayment, this will also trigger loadLoanInfo
    })

    const sliderTooltipFormatter = (v: number) =>
      `${formatNumber(v)} ${tc('month | months', v)}`

    const loanAverageInstallment = computed(() => {
      if (!loanInstallments.value) {
        return null
      }

      let amountSum = 0
      let monthsSum = 0
      loanInstallments.value.installmentSummary.forEach(item => {
        amountSum +=
          Number(item.installmentAmount) * Number(item.installmentCount)
        monthsSum += Number(item.installmentCount)
      })

      return Number((amountSum / monthsSum).toFixed(2))
    })

    const formattedLoanInstallment = computed(() => {
      if (!loanInstallments.value) {
        return '...'
      }
      return `${loanAverageInstallment.value} €`
    })

    const balloonAmount = computed(() => {
      if (
        loanInfo.value.type === 'balloon' &&
        loanInstallments.value?.installmentSummary
      ) {
        return Number(
          loanInstallments.value?.installmentSummary[
            loanInstallments.value.installmentSummary.length - 1
          ].installmentAmount
        )
      }
      return undefined
    })

    async function loadLoanInfo() {
      if (loadingLoanInfo.value || !classifiedId.value) {
        return
      }

      loadingLoanInfo.value = true
      try {
        const info = await loanService.getSantanderLoanInfo(
          classifiedId.value,
          loanDownpayment.value,
          loanDuration.value
        )
        loanInstallments.value = info
      } catch (error) {
        logger.captureError(error as any)
        snackbar.error(error.response.data.error)
      } finally {
        loadingLoanInfo.value = false
      }
    }

    const handleAmountChange = debounce(loadLoanInfo, DEBOUNCE_SWIFT)

    const onDragging = (v: number) => {
      if (loanInstallments.value) {
        loanInstallments.value = null
      }
      liveLoanDuration.value = v
    }

    watch(loanDuration, val => {
      liveLoanDuration.value = val
    })

    function handleGetALoanButtonClick() {
      showUserInfoModal.value = true
    }

    const inputWidth = computed(() => {
      const letterWidth = 11 // average
      if (loanDownpayment.value || loanDownpayment.value === 0) {
        return `${formatNumber(loanDownpayment.value).toString().length *
          letterWidth}px`
      }
      return `${letterWidth}px`
    })

    const minDownpayment = computed(
      () =>
        (loanInfo.value.minDownpaymentPercentage / 100) *
        classifiedRawPrice.value
    )
    const maxDownpayment = computed(
      () =>
        (loanInfo.value.maxDownpaymentPercentage / 100) *
        classifiedRawPrice.value
    )
    const onBlur = () => {
      showInput.value = true

      if (Number(loanDownpayment.value) <= minDownpayment.value) {
        loanDownpayment.value = minDownpayment.value
      } else if (Number(loanDownpayment.value) >= maxDownpayment.value) {
        loanDownpayment.value = maxDownpayment.value
      }

      if (
        Number(loanDownpayment.value) >
        classifiedRawPrice.value - minDownpayment.value
      ) {
        loanDownpayment.value = Number(
          (classifiedRawPrice.value - minDownpayment.value).toFixed(2)
        )
      }

      handleAmountChange()
    }

    const onFocus = () => {
      if (!showInput.value) {
        showInput.value = true
      }
    }

    const onLabelClick = async () => {
      showInput.value = true
      await nextTick()
      inputRef.value.focus()
    }

    const onInstallmentsClick = () => {
      if (loanInstallments.value) {
        showDetailsModal.value = true
      }
    }

    const onDetailsOk = async (e: Event) => {
      e.preventDefault()
      showDetailsModal.value = false
      await nextTick()
      showUserInfoModal.value = true
    }

    const onTargetUrl = (url: string) => {
      targetUrl.value = url
    }

    const sliderData = computed(() => {
      if (loanInfo.value.period.type === SantanderLoanPeriodType.ARRAY) {
        return loanInfo.value.period.values.map(p => Number(p))
      }
      return undefined
    })

    const sliderMarks = computed(() => {
      return {
        [minLoanDuration.value.toString()]: {
          label: ``
        },
        [maxLoanDuration.value.toString()]: {
          label: ``
        }
      }
    })

    return {
      formattedLoanInstallment,
      formatNumber,
      onDragging,
      loanDuration,
      liveLoanDuration,
      loadingLoanInfo,
      maxLoanDuration,
      minLoanDuration,
      loanAmount,
      sliderTooltipFormatter,
      sliderRef,
      sliderMarks,
      handleAmountChange,
      handleGetALoanButtonClick,
      onBlur,
      onLabelClick,
      inputWidth,
      showInput,
      loanDownpayment,
      inputRef,
      maxDownpayment,
      minDownpayment,
      loanInstallments,
      showDetailsModal,
      onInstallmentsClick,
      onFocus,
      showUserInfoModal,
      onDetailsOk,
      classifiedId,
      loanAverageInstallment,
      balloonAmount,
      targetUrl,
      onTargetUrl,
      SantanderLoanPeriodType,
      sliderData
    }
  }
})
